import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {getLoggedInUser} from '../utils/functions';
import {UserInfo} from '../types/interfaces';

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:5000/api';

type UiState = 'idle' | 'submitting' | 'success' | 'error';

export const AdminRegisterUser: React.FC = () => {
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [ui, setUi] = useState<UiState>('idle');
    const [msg, setMsg] = useState('');
    const navigate = useNavigate();

    const [form, setForm] = useState({
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        contactPostalAddress: '',
        contactPhysicalAddress: '',
        idType: '',
        employeeId: '',
        msisdn: ''
    });

    useEffect(() => {
        const init = async () => {
            const u = await getLoggedInUser();
            setUserInfo(u);
            if (!u) navigate('/error');
        };
        init();
    }, [navigate]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const {name, value} = e.target as HTMLInputElement;
        setForm(f => ({
            ...f,
            [name]: name === 'msisdn' ? value.replace(/\\D/g, '').slice(0, 8) : value
        }));
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setUi('submitting');
        setMsg('');
        const { msisdn, ...rest } = form;
        const payload: any = {
            ...rest,
            ...(msisdn ? { cellphone: `267${msisdn}` } : {})
        };
        try {
            const res = await fetch(`${API_BASE}/create-user`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userInfo?.token}` },
                body: JSON.stringify(payload)
            });
            const text = await res.text();
            if (res.ok) {
                setUi('success');
                setMsg(`User “${form.id}” created successfully 🎉`);
                setForm({ id: '', firstName: '', lastName: '', email: '', contactPostalAddress: '', contactPhysicalAddress: '', idType: '', employeeId: '', msisdn: '' });
            } else {
                setUi('error');
                setMsg(`Upstream error (${res.status}): ${text}`);
            }
        } catch (err: any) {
            setUi('error');
            setMsg(`Network / proxy error: ${err.message}`);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-8">
            <div className="max-w-xl mx-auto bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">Register New User</h2>
                {ui !== 'idle' && (
                    <div className={`mb-4 px-4 py-2 rounded text-sm ${ui === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{msg}</div>
                )}
                <form onSubmit={onSubmit} className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input name="id" value={form.id} onChange={onChange} placeholder="User ID" className="w-full px-3 py-2 border rounded" required />
                        <input name="firstName" value={form.firstName} onChange={onChange} placeholder="First Name" className="w-full px-3 py-2 border rounded" required />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input name="lastName" value={form.lastName} onChange={onChange} placeholder="Last Name" className="w-full px-3 py-2 border rounded" required />
                        <input name="email" value={form.email} onChange={onChange} placeholder="*****@btc.bw" type="email" className="w-full px-3 py-2 border rounded" required />
                    </div>
                    <input name="contactPostalAddress" value={form.contactPostalAddress} onChange={onChange} placeholder="Postal Address" className="w-full px-3 py-2 border rounded" required />
                    <input name="contactPhysicalAddress" value={form.contactPhysicalAddress} onChange={onChange} placeholder="Physical Address" className="w-full px-3 py-2 border rounded" required />
                    <select name="idType" value={form.idType} onChange={onChange} className="w-full px-3 py-2 border rounded" required>
                        <option value="">Select ID Type</option>
                        <option value="NATIONAL_ID">National ID</option>
                    </select>
                    <input name="employeeId" value={form.employeeId} onChange={onChange} placeholder="Employee ID" className="w-full px-3 py-2 border rounded" required />
                    <input name="msisdn" value={form.msisdn} onChange={onChange} placeholder="7*****" className="w-full px-3 py-2 border rounded" maxLength={8} inputMode="numeric" autoComplete="off" title="Enter 8 digits (e.g. 7xxxxxxx)" />
                    <div className="flex gap-2">
                        <button type="submit" disabled={ui === 'submitting'} className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 rounded">{ui === 'submitting' ? 'Registering…' : 'Register New User'}</button>
                        <button type="button" onClick={() => navigate('/admin')} className="flex-1 bg-gray-200 text-gray-800 font-semibold py-2 rounded">Back</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminRegisterUser;

